import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, LogOut, KeyRound, X, Church, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "../lib/utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetClose, SheetTrigger } from "./ui/sheet";

const linkCls = (isActive) => cn("flex items-center gap-3 px-4 min-h-[48px] rounded-xl text-sm font-semibold transition-colors border-l-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(224,64%,33%)]",
  isActive ? "bg-[hsl(224,64%,33%)] text-white border-[hsl(24,94%,50%)]" : "text-slate-700 hover:bg-slate-100 border-transparent");

const subCls = (isActive) => cn("flex items-center gap-2 pl-12 pr-4 min-h-[40px] rounded-xl text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(224,64%,33%)]",
  isActive ? "bg-[hsl(224,64%,33%)]/10 text-[hsl(224,64%,33%)] font-semibold" : "text-slate-600 hover:bg-slate-100");

const NavGroup = ({ l, prefix }) => {
  const { pathname } = useLocation();
  const active = l.children.some((c) => pathname.startsWith(c.to));
  const [open, setOpen] = useState(active);
  useEffect(() => { if (active) setOpen(true); }, [active]);
  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid={`${prefix}-${l.id}`} className={cn(linkCls(false), "w-full", active && "text-[hsl(224,64%,33%)]")}>
        <l.icon className="h-5 w-5 shrink-0" /><span className="flex-1 text-left">{l.label}</span>{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {open && <div className="mt-1 space-y-0.5">{l.children.map((c) => (
        <NavLink key={c.to} to={c.to} data-testid={`${prefix}-${c.id}`} className={({ isActive }) => subCls(isActive)}>
          {({ isActive }) => (<>{c.icon ? <c.icon className={cn("h-4 w-4 shrink-0", isActive ? "text-[hsl(24,94%,50%)]" : "text-slate-400")} /> : <span className={cn("h-1.5 w-1.5 rounded-full", isActive ? "bg-[hsl(24,94%,50%)]" : "bg-slate-300")} />}<span className="flex-1">{c.label}</span></>)}
        </NavLink>))}</div>}
    </div>
  );
};

export const NavList = ({ links, prefix = "nav" }) => (
  <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1" aria-label="Navigasi utama">
    {links.map((l) => l.children ? <NavGroup key={l.id} l={l} prefix={prefix} /> : (
      <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`${prefix}-${l.id}`} className={({ isActive }) => linkCls(isActive)}>
        {({ isActive }) => (<><l.icon className="h-5 w-5 shrink-0" /><span className="flex-1">{l.label}</span>{isActive && <span className="text-[10px] uppercase tracking-wider opacity-90" aria-current="page">Aktif</span>}</>)}
      </NavLink>
    ))}
  </nav>
);

export const NavFooter = ({ onGantiPassword, onLogout, prefix = "drawer" }) => (
  <div className="border-t border-slate-100 p-3 space-y-1">
    <button type="button" onClick={onGantiPassword} className="w-full flex items-center gap-3 px-4 min-h-[48px] rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100" data-testid={`${prefix}-ganti-password`}><KeyRound className="h-5 w-5" /> Ganti Password</button>
    <button type="button" onClick={onLogout} className="w-full flex items-center gap-3 px-4 min-h-[48px] rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50" data-testid={`${prefix}-logout-btn`}><LogOut className="h-5 w-5" /> Keluar</button>
  </div>
);

// Sidebar permanen desktop (>=768px)
export const DesktopSidebar = ({ links, user, roleLabel, onBrand, onGantiPassword, onLogout }) => (
  <aside className="hidden md:flex flex-col w-60 shrink-0 sticky top-0 h-screen bg-white border-r border-slate-200" data-testid="desktop-sidebar" aria-label="Sidebar navigasi">
    <button onClick={onBrand} className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 text-left" data-testid="sidebar-brand-btn">
      <div className="h-10 w-10 rounded-xl bg-[hsl(224,64%,33%)] flex items-center justify-center text-white shadow-sm shrink-0"><Church className="h-5 w-5" /></div>
      <div className="leading-tight"><div className="font-bold text-slate-900 text-base tracking-tight">SIAP GKPY</div><div className="text-[11px] text-slate-500">Sistem Informasi Administrasi & Pelayanan</div></div>
    </button>
    <NavList links={links} prefix="sidebar-nav" />
    <div className="px-5 py-2 text-xs text-slate-500 truncate" data-testid="sidebar-user">{user?.nama} · {roleLabel}</div>
    <NavFooter onGantiPassword={onGantiPassword} onLogout={onLogout} prefix="sidebar" />
  </aside>
);

// Hamburger + drawer kiri untuk mobile (<768px)
export const ResponsiveNavigation = ({ links, user, roleLabel, onGantiPassword, onLogout }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button type="button" aria-label="Buka menu navigasi" aria-controls="nav-drawer"
          className="md:hidden h-11 w-11 shrink-0 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors"
          data-testid="hamburger-btn">
          <Menu className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent id="nav-drawer" side="left" className="w-[85vw] max-w-[320px] p-0 flex flex-col [&>button]:hidden" data-testid="nav-drawer">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100 text-left">
          <div className="flex items-center justify-between gap-2">
            <div>
              <SheetTitle className="text-base font-bold text-slate-900">Menu</SheetTitle>
              <SheetDescription className="text-xs text-slate-500" data-testid="drawer-user">{user?.nama} · {roleLabel}</SheetDescription>
            </div>
            <SheetClose asChild>
              <button type="button" aria-label="Tutup menu" className="h-11 w-11 rounded-xl hover:bg-slate-100 flex items-center justify-center text-slate-600" data-testid="drawer-close-btn"><X className="h-5 w-5" /></button>
            </SheetClose>
          </div>
        </SheetHeader>
        <NavList links={links} prefix="nav" />
        <NavFooter onGantiPassword={() => { setOpen(false); onGantiPassword(); }} onLogout={() => { setOpen(false); onLogout(); }} prefix="drawer" />
      </SheetContent>
    </Sheet>
  );
};

export default ResponsiveNavigation;
